import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { engagementsApi, runsApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';

function DiffView({ runId }: { runId: string }) {
  const query = useQuery({ queryKey: ['run-diff', runId], queryFn: () => runsApi.diff(runId) });
  if (query.isLoading) return <p className="text-sm text-ink-muted">Loading comparison…</p>;
  if (query.isError) return <p className="text-sm text-danger">{friendlyErrorMessage(query.error)}</p>;
  const diff = query.data!.diff;
  return (
    <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
      <div>
        <p className="text-2xl font-semibold text-ink">{diff.fixed.length}</p>
        <p className="text-ink-muted">Fixed</p>
      </div>
      <div>
        <p className="text-2xl font-semibold text-ink">{diff.stillOpen.length}</p>
        <p className="text-ink-muted">Still open</p>
      </div>
      <div>
        <p className="text-2xl font-semibold text-danger">{diff.regressed.length}</p>
        <p className="text-ink-muted">Came back</p>
      </div>
      <div>
        <p className="text-2xl font-semibold text-ink">{diff.new.length}</p>
        <p className="text-ink-muted">New</p>
      </div>
    </div>
  );
}

export function Retests() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });
  const engagements = engagementsQuery.data?.engagements ?? [];

  const runQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['runs', e.id],
      queryFn: () => runsApi.list(e.id),
      enabled: engagements.length > 0,
    })),
  });

  if (engagementsQuery.isLoading) return <LoadingState />;
  if (engagementsQuery.isError) return <ErrorState error={engagementsQuery.error} onRetry={() => engagementsQuery.refetch()} />;

  const retests = engagements.flatMap((e, i) =>
    (runQueries[i]?.data?.runs ?? []).filter((r) => r.retestOfRunId).map((run) => ({ engagement: e, run })),
  );

  return (
    <div>
      <PageHeader title="Retests" description="Compare a retest against the run it followed up on — what's fixed, what's not, what came back." />
      {retests.length === 0 ? (
        <EmptyState
          icon={<History size={32} />}
          title="No retests yet"
          description="Start a retest from a completed run to see what's changed since last time."
        />
      ) : (
        <div className="space-y-3">
          {retests.map(({ engagement, run }) => (
            <Card key={run.id}>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle>{engagement.name}</CardTitle>
                  <p className="text-xs text-ink-muted">Retest of {run.retestOfRunId} · started {new Date(run.createdAt).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="brand" className="capitalize">
                    {run.status}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => setExpanded(expanded === run.id ? null : run.id)}>
                    {expanded === run.id ? 'Hide' : 'Compare'}
                  </Button>
                </div>
              </CardHeader>
              {expanded === run.id ? (
                <CardContent>
                  <DiffView runId={run.id} />
                </CardContent>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
